import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Feather, 
  Shield, 
  Zap, 
  TrendingDown, 
  ArrowRight, 
  CheckCircle2,
  Building2,
  Droplets,
  Factory,
  Construction,
  Dumbbell
} from "lucide-react";

const keyBenefits = [
  {
    icon: Dumbbell,
    stat: "2X",
    label: "Stronger than Steel",
    description: "Higher tensile strength for superior structural performance"
  },
  {
    icon: Feather,
    stat: "80%",
    label: "Lighter than Steel",
    description: "Dramatically reduced weight for easier handling"
  },
  {
    icon: TrendingDown,
    stat: "30-60%",
    label: "Cost Savings",
    description: "Significant savings compared to TMT bars"
  },
  {
    icon: Shield,
    stat: "100%",
    label: "Corrosion Resistant",
    description: "Complete immunity to rust and corrosion"
  },
  {
    icon: Zap,
    stat: "5X",
    label: "Faster Execution",
    description: "Quicker installation and project completion"
  }
];

const applications = [
  { icon: Construction, name: "Bridges & Flyovers" },
  { icon: Droplets, name: "Coastal Structures" },
  { icon: Factory, name: "Industrial Flooring" },
  { icon: Building2, name: "Tunnels & Metro" }
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* 1. Hero Section - Stronger. Lighter. Smarter */}
      <section className="relative overflow-hidden min-h-[600px] lg:min-h-[700px]" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/b3r5ac47_Untitled%20design%20%285%29.mp4" type="video/mp4" />
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 section-container py-24 md:py-36 lg:py-44">
          <div className="max-w-3xl">
            <div className="accent-line mb-8 animate-fade-up"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Barlow_Condensed'] leading-tight animate-fade-up animation-delay-100">
              Stronger. Lighter. Smarter.
              <span className="block text-yellow-400">The GFRP Revolution Beyond Steel</span>
            </h1>
            <p className="text-lg text-slate-200 mb-10 max-w-2xl animate-fade-up animation-delay-200">
              Glass Fiber Reinforced Polymer (GFRP) technology that outperforms traditional steel 
              in strength, durability, and cost-efficiency. Build infrastructure that lasts 100+ years.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up animation-delay-300">
              <Link to="/contact">
                <Button 
                  className="bg-yellow-400 text-white hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base"
                  data-testid="hero-cta-quote"
                >
                  Request Technical Details
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/products">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base"
                  data-testid="hero-cta-products"
                >
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Why Choose GFRP? Section */}
      <section className="section-spacing bg-slate-50" data-testid="benefits-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <div className="accent-line mx-auto mb-6"></div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Barlow_Condensed']">
              Why Choose GFRP?
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Superior engineering properties that deliver measurable advantages over traditional reinforcement
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {keyBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="stat-card group animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-12 h-12 bg-yellow-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-6 h-6 text-slate-900" />
                </div>
                <div className="text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-2">
                  {benefit.stat}
                </div>
                <div className="text-base font-semibold text-slate-800 mb-2">
                  {benefit.label}
                </div>
                <p className="text-sm text-slate-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Now Approved By PWDs & Metros Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="approved-by-section">
        <div className="section-container relative z-10">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Barlow_Condensed'] uppercase tracking-wide">
              Now Approved By
            </h2>
            <p className="text-yellow-400 text-xl sm:text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide">
              PWDs & Metros Across India
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 lg:gap-8">
            {[
              { name: "DMRC", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/mdbsh39t_Delhi%20Metro.png" },
              { name: "Madhya Pradesh PWD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/9yoleiii_MP%20PWD.png" },
              { name: "Maharashtra PWD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/zxxuz6ue_Maharashtra%20PWD.jpg" },
              { name: "Maharashtra WRD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/ky1w6r4f_Maharashtra%20Water%20Department.jpg" },
              { name: "Mumbai Metro", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/2v9xx31v_Mumbai%20Metro.jpg" },
              { name: "Surat Metro", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/g3zzczvo_Surat%20Metro.png" },
              { name: "Gujarat PWD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/30rvwp91_Gujarat%20PWD.png" },
              { name: "Karnataka PWD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/5vche0dr_Karnataka%20PWD.png" },
              { name: "Chennai Metro", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/f9l4jts0_images.jpg" },
              { name: "Rajasthan PWD", logo: "https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/63hdmivl_Rajasthan%20PWD.png" },
            ].map((org, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg p-4 lg:p-5 text-center flex flex-col items-center justify-center min-h-[140px] hover:shadow-lg transition-shadow group"
                data-testid={`approved-org-${index}`}
              >
                <img 
                  src={org.logo} 
                  alt={org.name}
                  className="h-16 w-16 object-contain mb-3 group-hover:scale-110 transition-transform"
                />
                <p className="text-slate-800 font-semibold text-xs lg:text-sm leading-tight">{org.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Product Category - PROBAR and PROMESH */}
      <section className="section-spacing bg-white" data-testid="products-teaser">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* PRO BAR */}
            <div className="relative overflow-hidden border border-slate-200 card-hover">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-15"
                style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/ogcxjclc_PHOTO-2026-01-28-20-03-45%203.jpg')" }}
              ></div>
              <div className="relative z-10 p-8 lg:p-12 bg-slate-50/90">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/4w2a5hez_PHOTO-2026-01-28-20-03-45%203.jpg"
                  alt="PROBAR Logo"
                  className="h-16 w-auto mb-6"
                />
                <p className="text-slate-600 mb-6">
                  High-strength GFRP reinforcement bars engineered to replace traditional TMT steel 
                  with superior corrosion resistance and lifecycle performance.
                </p>
                <ul className="space-y-3 mb-8">
                  {["Rust-proof & maintenance-free", "Up to 80% lighter than steel", "100+ year design life"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/products">
                  <Button 
                    variant="outline" 
                    className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase tracking-wider rounded-none"
                    data-testid="probar-link"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* PRO MESH */}
            <div className="relative overflow-hidden border border-slate-200 card-hover">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-15"
                style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/t85m8kxg_PHOTO-2026-01-28-20-03-44%203.jpg')" }}
              ></div>
              <div className="relative z-10 p-8 lg:p-12 bg-slate-50/90">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/qxwaz64x_PHOTO-2026-01-28-20-03-46.jpg"
                  alt="PROMESH Logo"
                  className="h-16 w-auto mb-6"
                />
                <p className="text-slate-600 mb-6">
                  Advanced GFRP mesh solutions for industrial floors, pavements, and structural panels 
                  delivering exceptional crack control and durability.
                </p>
                <ul className="space-y-3 mb-8">
                  {["Superior crack control", "Chemical resistant", "Easy installation"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/products">
                  <Button 
                    variant="outline" 
                    className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase tracking-wider rounded-none"
                    data-testid="promesh-link"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Applications Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="applications-section">
        <div className="section-container relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-12">
            <div>
              <div className="accent-line mb-6"></div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Barlow_Condensed']">
                Applications
              </h2>
              <p className="text-slate-400 mt-4 max-w-xl">
                GFRP solutions trusted in critical infrastructure projects worldwide
              </p>
            </div>
            <Link to="/applications">
              <Button 
                className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                data-testid="view-all-applications"
              >
                View All Applications
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {applications.map((app, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-6 lg:p-8 text-center hover:border-yellow-400 transition-colors"
                data-testid={`application-${index}`}
              >
                <div className="w-14 h-14 bg-yellow-400 rounded-none flex items-center justify-center mx-auto mb-4">
                  <app.icon className="w-7 h-7 text-slate-900" />
                </div>
                <span className="text-white font-medium">{app.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Collaboration with CBI Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="collaboration-section">
        <div className="section-container">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-10">
            {/* Left - Collaboration Info */}
            <div className="lg:w-1/2 flex flex-col justify-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] uppercase leading-tight mb-6">
                A New Chapter In Innovation<br />
                <span className="text-slate-700">BuildEdge Tech Collaborates With CBI</span>
              </h2>
              
              {/* Logos */}
              <div className="flex items-center gap-4 mb-6">
                {/* BuildEdge Tech Logo */}
                <div className="flex items-center gap-2 bg-white px-3 py-2 shadow-md border border-slate-100">
                  <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
                    <span className="font-bold text-slate-900 text-xl font-['Barlow_Condensed']">BE</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 text-xs font-['Barlow_Condensed'] tracking-tight block">
                      BUILDEDGE
                    </span>
                    <span className="font-bold text-slate-900 text-xs font-['Barlow_Condensed'] tracking-tight block">
                      TECH
                    </span>
                  </div>
                </div>
                
                {/* Ampersand */}
                <span className="text-3xl font-bold text-yellow-400">&</span>
                
                {/* CBI Logo */}
                <div className="bg-white p-2 shadow-md border border-slate-100 rounded-full">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_850edf24-7d37-4318-bda9-eeaaa74cae59/artifacts/5zr69y17_WhatsApp%20Image%202026-03-23%20at%2017.31.21.jpeg"
                    alt="Composite Building Innovations"
                    className="h-14 w-14 object-contain"
                  />
                </div>
              </div>
              
              {/* Description */}
              <div className="text-slate-600 text-sm lg:text-base leading-relaxed space-y-3">
                <p>
                  BuildEdge Tech proudly announces its strategic collaboration with <span className="font-semibold text-slate-900">Composite Building Innovations First (CBI)</span>, a global leader in advanced composite construction technologies exclusively for the Indian Market.
                </p>
                <p>
                  This partnership strengthens our mission to transform construction through next-generation GFRP solutions, combining our manufacturing capabilities with CBI's global expertise to deliver <span className="font-semibold text-slate-900">durable, corrosion-resistant, and sustainable reinforcement solutions</span> for India.
                </p>
                <p>
                  Together, we aim to <span className="font-semibold text-slate-900">drive innovation, promote eco-friendly practices, enhance structural longevity,</span> and replace conventional steel with advanced GFRP rebars and mesh.
                </p>
                <p>
                  This collaboration marks a step forward in building infrastructure that is <span className="font-semibold text-slate-900">stronger, smarter, and more sustainable.</span>
                </p>
              </div>
            </div>
            
            {/* Right - BIS Standards Panel */}
            <div className="lg:w-1/2 bg-slate-900 p-6 lg:p-8 rounded-lg flex flex-col justify-between">
              <div>
                <div className="bg-white p-4 mb-6 inline-block">
                  <p className="text-xs text-slate-500 mb-1">भारतीय मानक / Indian Standard</p>
                  <p className="text-slate-900 font-bold text-lg">IS 18256 : 2023</p>
                </div>
                
                <div className="bg-white/10 p-4 mb-6">
                  <p className="text-white/80 text-sm leading-relaxed">
                    कंक्रीट प्रबलन के लिए काँच रेशा प्रबलित पॉलिमर (जीएफआरपी) के ठोस गोल सरिए — विशिष्ट
                  </p>
                  <p className="text-white font-medium mt-2">
                    Solid Round Glass Fibre Reinforced Polymer (GFRP) Bars for Concrete Reinforcement — Specification
                  </p>
                </div>
                
                <p className="text-white/60 text-sm mb-2">ICS 83.120; 91.100.30</p>
                <p className="text-white/60 text-sm mb-6">© BIS 2023</p>
              </div>
              
              <div>
                {/* BIS Logo */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white rounded flex items-center justify-center">
                    <svg className="w-12 h-12" viewBox="0 0 100 100">
                      <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill="#1e40af" stroke="#1e40af"/>
                      <circle cx="50" cy="50" r="12" fill="#dc2626"/>
                      <text x="50" y="85" textAnchor="middle" fill="#1e40af" fontSize="8" fontWeight="bold">BIS</text>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">BUREAU OF INDIAN STANDARDS</p>
                    <p className="text-white/60 text-xs">NEW DELHI - 110002</p>
                  </div>
                </div>
                
                <a 
                  href="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/6ah246y0_IS18256_2023.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 w-full justify-center transition-colors"
                >
                  Download BIS Guideline
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Ready to Build Smarter Section (CTA) */}
      <section className="section-spacing bg-yellow-400" data-testid="cta-section">
        <div className="section-container text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
            Ready to Build Smarter?
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto mb-10 text-lg">
            Get technical specifications, pricing, and project consultation from our engineering team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                className="bg-slate-900 text-white hover:bg-slate-800 font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base"
                data-testid="cta-contact"
              >
                Contact Us Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919871070356">
              <Button 
                variant="outline"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base"
                data-testid="cta-call"
              >
                Call +91-9871070356
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
